import { useParams } from 'react-router-dom'
import fetchAPI from '../../../common/fetchAPI'
import useModal from '../../../common/hooks/useModal'
import useToast from '../../../common/hooks/useToast'
import ConfirmModalContent from '../../../components/common/ConfirmModalContent'
import ResourceList from '../../../components/admin/ResourceList'
import MessageSection from '../../../components/common/MessageSection'
import { ICONS } from '../../../common/constants'
import useMatchMutate from '../../../common/hooks/useMatchMutate'
import useAuth from '../../../common/hooks/useAuth'
import InputModalContent from '../../../components/common/InputModalContent'
import SectionContainer from '../../../components/common/SectionContainer'

export default function ShopLocationsList({ enablePullToRefresh }) {
    const { shopifyShopId } = useParams()
    const auth = useAuth()
    const setToast = useToast()
    const { setModal, setModalLoading, isModalLoading } = useModal()
    const matchMutate = useMatchMutate()

    async function onRename(id) {
        setModal(
            <InputModalContent
                label="Location name"
                confirmText="Save"
                onConfirm={async ({ text }) => {
                    if (isModalLoading) return
                    setModalLoading(true)
                    const url = `/v1/shop/shops/${shopifyShopId}/locations/${id}?fields=name`
                    const { error } = await fetchAPI(
                        url,
                        {
                            name: text,
                        },
                        'PATCH',
                    )
                    setModalLoading(false)
                    if (error) {
                        setToast(error, 'alert')
                        setModal(null)
                        return
                    }
                    setModal(null)
                    setToast('Saved')
                    matchMutate(/\/v1\/shop\/shops/)
                }}
            />,
            'Location name',
        )
    }

    async function onDelete(id) {
        setModal(
            <ConfirmModalContent
                text={
                    'All quantities associated with this location will be deleted. This cannot be undone.'
                }
                onConfirm={async () => {
                    if (isModalLoading) return
                    setModalLoading(true)
                    const { error } = await fetchAPI(
                        `/v1/shop/shops/${shopifyShopId}/locations/${id}`,
                        {},
                        'DELETE',
                    )
                    setModalLoading(false)
                    if (error) {
                        setModal(null)
                        setToast(error, 'alert')
                        return
                    }
                    setToast('Deleted')
                    setModal(null)
                    matchMutate(/\/v1\/shop\/shops/)
                }}
            />,
            'Are you sure you want to delete this location?',
        )
    }

    return (
        <>
            {auth.isAdmin && (
                <SectionContainer>
                    <MessageSection type="info">
                        These locations are in-app only.
                    </MessageSection>
                </SectionContainer>
            )}
            <ResourceList
                enablePullToRefresh={enablePullToRefresh}
                renderMainButton
                newItemLabel="New location"
                apiUrl={`/v1/shop/shops/${shopifyShopId}/locations`}
                initialQuery={{
                    fields: 'name',
                }}
                newItemPath={`/shop/shopify-shops/${shopifyShopId}/locations/new`}
                actions={[
                    {
                        title: 'Quantities',
                        link: '/shop/shopify-shops/:shopifyShopId/locations/:item.id/inventory-items',
                    },
                    {
                        title: 'Permissions (Beta)',
                        link: '/settings/permissions/shop_location/:item.id',
                        icon: ICONS.LOCK_GRAY,
                        getHide: (item) => auth.isClient || item.isShared,
                    },
                    {
                        title: 'Rename',
                        onClick: (item) => onRename(item.id),
                        icon: ICONS.EDIT_GRAY,
                    },
                    {
                        title: 'Delete',
                        onClick: (item) => onDelete(item.id),
                        icon: ICONS.TRASH_RED,
                        type: 'alert',
                    },
                ]}
                fields={[
                    {
                        column: 1,
                        getValue: (item) => item.name,
                        getClassName: () => 'title',
                    },
                ]}
            />
        </>
    )
}
